import React from "react"

import { Badge } from "@coval/ui/badge"
import { ChoiceCard, ChoiceCardGroup } from "@coval/ui/choice-card"
import { MessageSquare, Wand2 } from "lucide-react"

import type { ImprovementFocus } from "../optimization/optimization"

interface ImprovementFocusSelectorProps {
  value: ImprovementFocus
  onChange: (focus: ImprovementFocus) => void
  hasFeedback: boolean
  hasHighMachineDisagreement: boolean
}

interface FocusOption {
  value: ImprovementFocus
  label: string
  description: string
  icon: React.ReactNode
  recommended?: boolean
  disabled?: boolean
  disabledReason?: string
}

export const ImprovementFocusSelector = ({
  value,
  onChange,
  hasFeedback,
  hasHighMachineDisagreement,
}: ImprovementFocusSelectorProps) => {
  const headingId = React.useId()
  const options: FocusOption[] = [
    {
      value: "general",
      label: "General suggestions",
      description: "Suggest clarity, structure, and best practice improvements",
      icon: <Wand2 className="size-4 shrink-0" aria-hidden="true" />,
      recommended: !hasFeedback,
    },
    {
      value: "feedback",
      label: "Address feedback issues",
      description: "Focus on reviewer notes and human-AI label differences",
      icon: <MessageSquare className="size-4 shrink-0" aria-hidden="true" />,
      recommended: hasFeedback && hasHighMachineDisagreement,
      disabled: !hasFeedback,
      disabledReason:
        "No annotations available. Add annotations to enable this option.",
    },
  ]

  return (
    <div className="space-y-2">
      <p
        id={headingId}
        className="text-xs text-coval-text-muted font-medium uppercase"
      >
        Suggestion Focus
      </p>
      <ChoiceCardGroup aria-labelledby={headingId} className="grid gap-2">
        {options.map((option) => (
          <ChoiceCard
            key={option.value}
            title={
              <span className="inline-flex items-center gap-2">
                {option.icon}
                {option.label}
              </span>
            }
            badge={
              option.recommended && !option.disabled ? (
                <Badge variant="info" className="h-5 px-1.5 text-xs">
                  Recommended
                </Badge>
              ) : undefined
            }
            description={option.description}
            selected={value === option.value}
            disabled={option.disabled}
            onSelect={() => onChange(option.value)}
          >
            {/* Written out rather than in a tooltip: a disabled card takes no
                pointer or focus, so a tooltip on it would never open. */}
            {option.disabled && option.disabledReason && (
              <p className="text-xs text-coval-text-muted">
                {option.disabledReason}
              </p>
            )}
          </ChoiceCard>
        ))}
      </ChoiceCardGroup>
    </div>
  )
}
